/**
 * Central schema.org helpers — every page-level JSON-LD emitted by the app
 * MUST reference these @ids so Google can wire the Physician, Clinic, Site
 * and per-page entities into a single knowledge graph.
 *
 * Do NOT redefine the Physician or MedicalClinic bodies here — they live
 * canonically in /public/index.html and are referenced by @id everywhere.
 */

export const SITE = "https://drshantanugupta.com";

/** Canonical @id references shared across the entire site. */
export const REF = {
  physician: `${SITE}/#physician`,
  physicianManisha: `${SITE}/#physician-manisha`,
  clinic: `${SITE}/#hospital`,
  website: `${SITE}/#website`,
};

/** Author sub-graph — always link via @id, never restate the body. */
export const AUTHOR_REF = { "@type": "Person", "@id": REF.physician, name: "Dr. Shantanu Kumar Gupta", url: `${SITE}/about` };
export const AUTHOR_REF_MANISHA = { "@type": "Person", "@id": REF.physicianManisha, name: "Dr. Manisha Mishra", url: `${SITE}/team/dr-manisha-mishra` };
export const PUBLISHER_REF = { "@type": "MedicalClinic", "@id": REF.clinic, name: "Shantanu Netralaya Eye Hospital" };
export const PROVIDER_REF = { "@id": REF.clinic };

/**
 * Best-guess author picker for a blog post — Manisha handles complex
 * cataract / squint / paediatric ophthalmology content; everything else
 * defaults to Dr. Shantanu.  Callers may override by setting
 * `post.author_slug = "manisha" | "shantanu"` in the CMS.
 */
export const authorForPost = (post) => {
  if (!post) return AUTHOR_REF;
  const slug = (post.author_slug || "").toLowerCase();
  if (slug === "manisha") return AUTHOR_REF_MANISHA;
  if (slug === "shantanu") return AUTHOR_REF;
  const haystack = [
    (post.category || "").toLowerCase(),
    ...(post.tags || []).map((t) => (t || "").toLowerCase()),
    (post.title || "").toLowerCase(),
    (post.slug || "").toLowerCase(),
  ].join(" ");
  if (/(squint|strabismus|paediatric|pediatric|amblyop|bhengapan|manisha|complex cataract|complicated cataract)/i.test(haystack)) {
    return AUTHOR_REF_MANISHA;
  }
  return AUTHOR_REF;
};

/** Build a BreadcrumbList JSON-LD sub-graph.
 *  crumbs = [{name, path}]   (path relative to SITE, e.g. "/about") */
export const breadcrumb = (crumbs) => ({
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: `${SITE}${c.path}`,
  })),
});

/** Wrap a graph array into a top-level `@context` object. */
export const graph = (nodes) => ({ "@context": "https://schema.org", "@graph": nodes.filter(Boolean) });

/**
 * Extract a YouTube video id from either a full URL, shorts URL or raw
 * 11-character id.  Shared helper so schema builders and embeds stay in
 * sync.
 */
export const extractYouTubeId = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s;
  const m = s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : "";
};

/**
 * Build a VideoObject JSON-LD node for a YouTube video (regular, Short or
 * Reel-style vertical).  Returns null if a valid YouTube id cannot be
 * derived so callers can safely spread the result into a schema graph.
 *
 * @param {object} opts
 * @param {string} opts.id        Route-unique fragment (e.g. "shorts-abc").
 * @param {string} opts.pagePath  Canonical page path (e.g. "/videos").
 * @param {string} opts.name      Human-readable video title.
 * @param {string} [opts.description]  Description for the rich result.
 * @param {string} [opts.videoRef]   Raw YouTube URL or 11-char id.
 * @param {string} [opts.thumbnailUrl] Override thumbnail (falls back to
 *   youtube's own hqdefault).
 * @param {string} [opts.uploadDate]  ISO date the video was published.
 * @param {number} [opts.durationSec] Optional video duration in seconds.
 */
export const videoObject = ({
  id,
  pagePath,
  name,
  description,
  videoRef,
  thumbnailUrl,
  uploadDate,
  durationSec,
}) => {
  const ytId = extractYouTubeId(videoRef);
  if (!ytId) return null;
  const iso = uploadDate ? new Date(uploadDate).toISOString() : undefined;
  const durationIso = typeof durationSec === "number" && durationSec > 0
    ? `PT${Math.floor(durationSec / 60)}M${durationSec % 60}S`
    : undefined;
  return {
    "@type": "VideoObject",
    "@id": `${SITE}${pagePath}#${id}`,
    name,
    description:
      description ||
      `Educational eye-care video from Dr. Shantanu Kumar Gupta, Ophthalmologist, Varanasi.`,
    thumbnailUrl: thumbnailUrl || `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`,
    uploadDate: iso,
    contentUrl: `https://www.youtube.com/watch?v=${ytId}`,
    embedUrl: `https://www.youtube.com/embed/${ytId}`,
    duration: durationIso,
    publisher: PUBLISHER_REF,
    author: AUTHOR_REF,
    inLanguage: ["en-IN", "hi-IN"],
  };
};

/**
 * Reusable Speakable schema block — flags h1/h2 + medically-vetted answer
 * regions as the ones Google Assistant / Alexa should read aloud in
 * voice-search responses. Cheap SEO win most corporate hospital sites skip.
 */
export const SPEAKABLE = {
  "@type": "SpeakableSpecification",
  cssSelector: ["h1", "h2", "h3", ".speakable"],
};

/**
 * MedicalClinic schema with aggregateRating — attach to every page-level
 * JSON-LD graph so Google shows review stars + the "Book" pin in Local
 * Pack results.  The Physician node lives in /public/index.html; here we
 * emit a compact `MedicalClinic` reference plus the rating.
 *
 * Update `aggregateRating` counts here whenever new Google reviews land —
 * this is a single source of truth used across every page.
 */
export const CLINIC_NODE = {
  "@type": ["MedicalClinic", "MedicalBusiness", "LocalBusiness"],
  "@id": REF.clinic,
  name: "Shantanu Netralaya Eye Hospital",
  alternateName: ["Shantanu Netralaya", "Shantanu Eye Hospital", "शान्तनु नेत्रालय"],
  url: SITE,
  logo: `${SITE}/logo.png`,
  // ImageObject gallery — Google's local pack, entity cards and rich results
  // surface these images. Each entry carries an explicit alt-caption for
  // accessibility + entity understanding. Sourced from `lib/clinicPhotos`
  // (Duplicated as URLs here rather than imported so this file stays
  // side-effect-free and importable from anywhere in the app.)
  image: [
    { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/55a2lhi6_887d09f6-aac0-453c-bc07-62fb61dcc9cc.jpg", caption: "Shantanu Netralaya Eye Hospital — building exterior at 40 Deen Dayal Colony, Kabir Nagar Road, Durgakund, Varanasi", representativeOfPage: true },
    { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/endnogv7_20250529_43212PMByGPSMapCamera.webp", caption: "Sterile operating theatre with Zeiss surgical microscope and MICS phacoemulsification setup" },
    { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/30fr6xvy_IMG_5184.webp", caption: "Micro-incision cataract surgery in progress — Dr. Shantanu Kumar Gupta operating" },
    { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/4rohy1sf_IMG_4525.webp", caption: "Consultation and diagnostic room — slit-lamp examination, autorefractor and IOP measurement" },
    { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/evj0ubnz_IMG_1862%202.webp", caption: "3nethra Classic fundus camera used for medical-retina imaging (diabetic retinopathy, AMD, glaucoma disc analysis)" },
    { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/4khj7cak_IMG_4992.webp", caption: "Reception desk with LED-lit curved counter and cashless mediclaim assistance" },
    { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/0dbirh7e_IMG_4996.webp", caption: "Patient waiting area with Tecnis Eyhance and Vivinex premium IOL information" },
    { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/g4zw2vhy_IMG_4994.webp", caption: "Day-care recovery ward for same-day-discharge cataract patients" },
  ],
  photo: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/55a2lhi6_887d09f6-aac0-453c-bc07-62fb61dcc9cc.jpg",
  telephone: "+91-8318665154",
  priceRange: "₹₹",
  currenciesAccepted: "INR",
  paymentAccepted: "Cash, Credit Card, Debit Card, UPI, Ayushman Bharat (PM-JAY), Cashless mediclaim via Bajaj Allianz, SBI General, Go Digit, Manipal Cigna, Reliance General, Universal Sompo, HDFC ERGO, TATA AIG, Care Health, Future Generali, Acko, Navi, Galaxy Health, Safeway, Medi Assist, Link-K Insurance TPA, East West Assist, AKNA Health Insurance TPA, FHPL, Heritage Health, Health India Insurance TPA",
  currenciesAccepted: "INR",
  address: {
    "@type": "PostalAddress",
    streetAddress: "40, Deen Dayal Colony, Kabir Nagar Road, Near Durgakund",
    addressLocality: "Varanasi",
    addressRegion: "Uttar Pradesh",
    postalCode: "221005",
    addressCountry: "IN",
  },
  geo: { "@type": "GeoCoordinates", latitude: 25.2836, longitude: 82.9871 },
  hasMap: "https://maps.app.goo.gl/uRR8gCzUGBGbjDx48",
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], opens: "10:00", closes: "20:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "10:00", closes: "14:00" },
  ],
  medicalSpecialty: ["Ophthalmic", "Ophthalmology", "PediatricOphthalmology", "Strabismus"],
  // Practice status (Feb 2026) — safe defaults confirmed by the clinic.
  isAcceptingNewPatients: true,
  smokingAllowed: false,
  publicAccess: true,
  // Contact points — appointments vs enquiries.
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "reservations",
      telephone: "+91-8318665154",
      email: "shantanunetralaya@gmail.com",
      availableLanguage: ["English", "Hindi", "Bhojpuri"],
      areaServed: "IN",
    },
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      telephone: "+91-8318665154",
      contactOption: ["TollFree", "HearingImpairedSupported"],
      availableLanguage: ["English", "Hindi"],
    },
  ],
  // Institutional coverages we've publicly confirmed on the site — kept
  // narrow: only PM-JAY is publicly declared. TPA cashless is separate.
  healthPlanNetworkId: "PM-JAY-empanelled",
  // Machine-readable services list — mirrors what the site's Expertise
  // pages describe. Each entry has an ICD-10 / SNOMED code where relevant.
  availableService: [
    { "@type": "MedicalProcedure", name: "Micro-incision cataract surgery (MICS phacoemulsification)", procedureType: "https://schema.org/SurgicalProcedure", code: { "@type": "MedicalCode", codeValue: "13590009", codingSystem: "SNOMED-CT" } },
    { "@type": "MedicalProcedure", name: "Toric IOL implantation for astigmatism correction", procedureType: "https://schema.org/SurgicalProcedure" },
    { "@type": "MedicalProcedure", name: "Trifocal / EDOF premium IOL implantation (designed to reduce spectacle dependence)", procedureType: "https://schema.org/SurgicalProcedure" },
    { "@type": "MedicalProcedure", name: "Intravitreal anti-VEGF injection (wet AMD / diabetic macular oedema)", procedureType: "https://schema.org/TherapeuticProcedure", code: { "@type": "MedicalCode", codeValue: "H35.32", codingSystem: "ICD-10" } },
    { "@type": "MedicalProcedure", name: "Pan-retinal photocoagulation (PRP) laser for proliferative diabetic retinopathy", procedureType: "https://schema.org/TherapeuticProcedure", code: { "@type": "MedicalCode", codeValue: "H35.03", codingSystem: "ICD-10" } },
    { "@type": "MedicalProcedure", name: "Optical coherence tomography (OCT) imaging", procedureType: "https://schema.org/DiagnosticProcedure" },
    { "@type": "MedicalProcedure", name: "Squint / strabismus surgery (adult & paediatric)", procedureType: "https://schema.org/SurgicalProcedure" },
    { "@type": "MedicalProcedure", name: "Amblyopia (lazy eye) therapy and paediatric refraction", procedureType: "https://schema.org/TherapeuticProcedure" },
    { "@type": "MedicalProcedure", name: "Corneal transplantation and complex-cornea evaluation", procedureType: "https://schema.org/SurgicalProcedure" },
    { "@type": "MedicalProcedure", name: "Dry-eye assessment and lipid-based tear management", procedureType: "https://schema.org/TherapeuticProcedure" },
    { "@type": "MedicalProcedure", name: "AI-supervised educational eye symptom triage (online)", procedureType: "https://schema.org/DiagnosticProcedure" },
  ],
  // Explicit catchment — Google uses this to bind us to local-pack results
  // for every named area. Kept flat (not nested) so each entity is discovered
  // independently by structured-data parsers.
  areaServed: [
    { "@type": "City", name: "Varanasi", containedInPlace: { "@type": "State", name: "Uttar Pradesh" } },
    { "@type": "City", name: "Kashi" },
    { "@type": "AdministrativeArea", name: "Purvanchal (eastern Uttar Pradesh)" },
    // Districts of eastern Uttar Pradesh routinely served
    { "@type": "AdministrativeArea", name: "Chandauli district, Uttar Pradesh" },
    { "@type": "AdministrativeArea", name: "Jaunpur district, Uttar Pradesh" },
    { "@type": "AdministrativeArea", name: "Mirzapur district, Uttar Pradesh" },
    { "@type": "AdministrativeArea", name: "Ghazipur district, Uttar Pradesh" },
    { "@type": "AdministrativeArea", name: "Sant Ravidas Nagar (Bhadohi) district, Uttar Pradesh" },
    { "@type": "AdministrativeArea", name: "Mau district, Uttar Pradesh" },
    { "@type": "AdministrativeArea", name: "Ballia district, Uttar Pradesh" },
    { "@type": "AdministrativeArea", name: "Azamgarh district, Uttar Pradesh" },
    { "@type": "AdministrativeArea", name: "Sonbhadra district, Uttar Pradesh" },
    { "@type": "AdministrativeArea", name: "Deoria district, Uttar Pradesh" },
    // Adjoining Bihar districts (Feb 2026 catchment expansion)
    { "@type": "AdministrativeArea", name: "Kaimur district, Bihar" },
    { "@type": "AdministrativeArea", name: "Rohtas district, Bihar" },
    { "@type": "AdministrativeArea", name: "Buxar district, Bihar" },
  ],
  // 200 km geographic service radius from the Durgakund clinic — this is a
  // machine-readable statement of catchment, complementary to areaServed.
  serviceArea: {
    "@type": "GeoCircle",
    geoMidpoint: { "@type": "GeoCoordinates", latitude: 25.2836, longitude: 82.9871 },
    geoRadius: "200000", // metres
    description: "200 km radius from Durgakund, Varanasi — covering Purvanchal (eastern Uttar Pradesh) and adjoining Bihar districts.",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "900",
    bestRating: "5",
    worstRating: "1",
  },
  sameAs: [
    "https://www.google.com/search?q=Shantanu+Netralaya+Varanasi",
    "https://www.google.com/maps?cid=1796062888296972207",
    "https://www.justdial.com/Varanasi/Shantanu-Netralaya-Near-By-Kabir-Nagar-Jrs-Tutorials-Durgakund/0542PX542-X542-130410220015-U4S9_BZDET",
    "https://www.practo.com/varanasi/doctor/dr-shantanu-kumar-gupta-ophthalmologist",
    "https://www.practo.com/varanasi/doctor/manisha-mishra-ophthalmologist",
    "https://www.instagram.com/shantanunetralaya/",
    "https://www.youtube.com/@SHANTANU-NETRALAYA",
    "https://www.facebook.com/shantanunetralaya/",
    "https://shantanunetralaya.com",
  ],
};

